import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { planShortPeriod } from '../utils/posLandingUtil';
import PosLandingTillDemo from './PosLandingTillDemo';
import { LANDING_BRAND_NAME, LANDING_HEADLINE, LANDING_SECTIONS } from '../constants/posLandingConst';

/**
 * The first screen: the pitch, the two calls to action, the starting price,
 * and the working till beside them.
 *
 * @param {object} props
 * @param {string} props.trialLabel - e.g. '7 days'; '' when no trial plan exists
 * @param {object|null} props.entryPlan - the "Then ₱199/month" plan
 * @param {{plan: object, percent: number}|null} props.bestSaving - the "Save 33% yearly" plan
 * @param {(plan: object) => void} props.onShowPlan - selects that plan on the pricing card
 * @param {() => void} props.onTryFree - selects the free trial on the pricing card
 * @returns {JSX.Element}
 */
function PosLandingHero({
  trialLabel, entryPlan, bestSaving, onShowPlan, onTryFree,
}) {
  const { config } = useConfig();

  /**
   * Picks the best-saving plan, then scrolls to the pricing card to show it.
   *
   * @param {React.MouseEvent} event
   * @returns {void}
   */
  const handleSavingClick = (event) => {
    event.preventDefault();
    onShowPlan(bestSaving.plan);
    document.getElementById(LANDING_SECTIONS.pricing)?.scrollIntoView({ block: 'start' });
  };

  return (
    <section className="hero">
      <div className="wrap hero-grid">
        <div>
          <div className="badge">
            <i />
            {' '}
            {trialLabel ? `Try ${trialLabel} free` : 'Try it free'} — no card, no e-wallet, no connect needed
          </div>
          <h1>{LANDING_HEADLINE.map((line) => <span key={line}>{line}</span>)}</h1>
          <p className="lede">
            The all-in-one POS for court facilities, cafés, retail shops, and beyond. Ring up customers in seconds, track every item automatically, and ditch the manual spreadsheets.
          </p>
          <div className="hero-cta">
            <a href={`#${LANDING_SECTIONS.pricing}`} className="btn btn-primary btn-lg" onClick={onTryFree}>Try {LANDING_BRAND_NAME} Free</a>
            <a href={`#${LANDING_SECTIONS.work}`} className="btn btn-ghost btn-lg">See it work</a>
          </div>
          {entryPlan && (
            <p className="hero-price">
              Then <b className="money">{formatMoney(entryPlan.price, config)}</b>{planShortPeriod(entryPlan)} only, every feature included
              {bestSaving && (
                <>
                  {' · '}
                  <a href={`#${LANDING_SECTIONS.pricing}`} className="yearly-link" onClick={handleSavingClick}>
                    Save {bestSaving.percent}% {bestSaving.plan.name.toLowerCase()}
                  </a>
                </>
              )}
            </p>
          )}
        </div>

        <PosLandingTillDemo />
      </div>
    </section>
  );
}

export default PosLandingHero;
